import { Asset, AssetType, Commercial, Edit, EditType } from '@talon/web/lib/api';
import { AssetSelectorModal, EditReviewModal, TalonVideoPlayer, WatchVideoModal } from '@talon/web/src/components';
import { useProjectStore } from '@talon/web/src/context';
import { Anchor, Box, BoxProps, Button, CheckBox, DropType, Select, Text } from 'grommet';
import { Close, Inspect } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { borderRadiusSize, tooltipProps } from 'src/theme';
import { EditableText, TalonInput } from '..';

interface IEditRowProps {
  edit: Edit;
  commercial: Commercial;
  checked: boolean;
  onChecked: () => void;
  updateEdit: (update: Partial<Edit>) => Promise<void>;
  columnWidths?: BoxProps['width'][];
}

export const EditRow = observer(
  ({ commercial, edit, onChecked, checked, updateEdit, columnWidths = [] }: IEditRowProps) => {
    /** Context **/
    const projectStore = useProjectStore();

    /** State **/
    const [showModal, setShowModal] = useState(false);
    const [showAssetSelector, setShowAssetSelector] = useState(false);
    const [showEditReviewModal, setShowEditReviewModal] = useState(false);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>(edit.asset_id || undefined);
    const [, setAssetThumbnails] = useState<Map<string, string>>(new Map());

    /** Computed **/
    const videoAssets = useMemo(
      () => (projectStore.currentProject?.assets || []).filter((asset) => asset.type === AssetType.Video),
      [projectStore.currentProject?.assets]
    );

    /** Refs **/

    // CommercialEditRow passes a brand-new updateEdit closure on every one of its
    // re-renders. Reading it via ref (instead of putting it in the effect below's
    // dependency array) means an unrelated parent re-render doesn't re-trigger the
    // signed-URL fetch for every edit's asset.
    const updateEditRef = useRef(updateEdit);
    useEffect(() => {
      updateEditRef.current = updateEdit;
    }, [updateEdit]);

    /** Methods **/

    const handleAssetSelect = async (asset: Asset) => {
      setSelectedAssetId(asset.id);
      const url = await projectStore.getSignedAssetUrl(asset.id);
      setVideoUrl(url || null);
      await updateEdit({ asset_id: asset.id });
      setShowAssetSelector(false);
    };

    const handleClearAsset = async () => {
      setSelectedAssetId(undefined);
      setVideoUrl(null);
      await updateEdit({ asset_id: undefined });
    };

    /** Effects **/

    // Get video URL from asset when component mounts or asset changes
    useEffect(() => {
      const loadVideoUrl = async () => {
        if (edit.asset_id) {
          // Only proceed if this edit belongs to the current project
          const editBelongsToCurrentProject = edit.project_id === projectStore.currentProject?.id;
          if (!editBelongsToCurrentProject) {
            // Don't do anything if the edit doesn't belong to current project (e.g., during project switch)
            return;
          }

          // Only check if asset exists if we have assets loaded (not empty array from project change)
          // If project has assets and our asset isn't in the list, it was deleted
          const hasAssetsLoaded = projectStore.currentProject?.assets && projectStore.currentProject.assets.length > 0;
          const assetExists = videoAssets.some((asset) => asset.id === edit.asset_id);

          if (hasAssetsLoaded && !assetExists) {
            // Asset was deleted, clear the reference silently
            console.log('Asset no longer exists, clearing reference');
            setVideoUrl(null);
            setSelectedAssetId(undefined);
            await updateEditRef.current({ asset_id: undefined });
            return;
          }

          // If assets haven't loaded yet, skip loading the URL (will retry when assets load)
          if (!hasAssetsLoaded) {
            return;
          }

          try {
            const url = await projectStore.getSignedAssetUrl(edit.asset_id);
            setVideoUrl(url || null);
          } catch (error) {
            console.error('Error loading asset URL (asset may have been deleted):', error);
            setVideoUrl(null);
            setSelectedAssetId(undefined);
            // Clear the stale asset_id reference
            await updateEditRef.current({ asset_id: undefined });
          }
        } else {
          setVideoUrl(null);
        }
      };
      loadVideoUrl();
      // updateEdit deliberately excluded - see updateEditRef above.
    }, [edit.asset_id, projectStore, videoAssets]);

    // Load thumbnails for video assets when selector opens
    useEffect(() => {
      if (showAssetSelector && videoAssets.length > 0) {
        const loadThumbnails = async () => {
          const thumbnailMap = new Map<string, string>();
          for (const asset of videoAssets) {
            if (asset.thumbnail_path) {
              const url = await projectStore.getAssetThumbnailUrl(asset.id);
              if (url) thumbnailMap.set(asset.id, url);
            }
          }
          setAssetThumbnails(thumbnailMap);
        };
        loadThumbnails();
      }
    }, [showAssetSelector, projectStore, videoAssets]);

    /** Render **/
    return (
      <Box>
        <Box direction="row" justify="between" align="center" gap="medium" pad="small">
          <Box width={columnWidths[0]} align="center">
            <CheckBox
              onChange={onChecked}
              checked={checked}
              disabled={edit.type === EditType.MainAsset}
              color="white"
            />
          </Box>

          {/* Edit Name */}
          <Box width={columnWidths[1]}>
            <EditableText val={edit.title || ''} onChange={(val) => updateEdit({ title: val })} />
          </Box>

          {/* Edit Type */}
          <Box width={columnWidths[2]}>
            <Select
              value={edit.type}
              onChange={({ value }) => updateEdit({ type: value as EditType })}
              options={Object.values(EditType)}
            />
          </Box>

          {/* Duration */}
          <Box width={columnWidths[3]}>
            <TalonInput
              val={edit.duration}
              inputType="number"
              placeholder="Duration"
              onChange={(val) => {
                const n = parseInt(val as string, 10);
                if (!Number.isNaN(n)) updateEdit({ duration: n });
              }}
            />
          </Box>

          {/* Paid Usage */}
          <Box width={columnWidths[4]} align="center">
            <CheckBox checked={edit.is_paid} onChange={(e) => updateEdit({ is_paid: e.target.checked })} />
          </Box>

          {/* AdId/ISCI */}
          <Box width={columnWidths[5]}>
            <TalonInput val={edit.ad_id || ''} placeholder="AdId/ISCI" onChange={(val) => updateEdit({ ad_id: val })} />
          </Box>

          {/* Asset */}
          <Box width={columnWidths[6]} direction="row" gap="small" align="center" justify="center">
            {videoUrl ? (
              <Box
                style={{
                  position: 'relative',
                }}
                width="200px"
                height="112px"
                direction="row"
                align="center"
                justify="center"
                round={borderRadiusSize}
                background="black"
              >
                <TalonVideoPlayer
                  url={videoUrl}
                  height="112px"
                  thumbnailOnly={true}
                  controls={false}
                  onThumbnailClick={() => setShowModal(true)}
                  onClearAsset={handleClearAsset}
                  onError={(error) => {
                    console.error('Video player error:', error);
                  }}
                />
                <Button
                  icon={<Close size="10px" color="white" />}
                  onClick={handleClearAsset}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '5px',
                    minWidth: 'auto',
                    minHeight: 'auto',
                    borderRadius: '100%',
                    padding: '2px',
                    border: '1px solid white',
                    zIndex: 10,
                  }}
                  pad="2px"
                  plain={false}
                  size="small"
                  tip="Remove asset"
                />
              </Box>
            ) : (
              <Button onClick={() => setShowAssetSelector(true)} plain>
                <Anchor margin="none">
                  <Box
                    style={{
                      position: 'relative',
                    }}
                    width="200px"
                    height="111px"
                    direction="row"
                    align="center"
                    justify="center"
                    round={borderRadiusSize}
                    background="light-1"
                    border={{ size: '2px', style: 'dashed', color: 'light-4' }}
                  >
                    <Text color="dark-3">Select Asset</Text>
                  </Box>
                </Anchor>
              </Button>
            )}
          </Box>

          <Box width={columnWidths[7]} direction="row" justify="end" align="center" gap="small">
            <Button
              icon={<Inspect />}
              hoverIndicator
              tip={{
                content: 'Edit Reviews',
                dropProps: tooltipProps as DropType,
                plain: true,
              }}
              onClick={() => setShowEditReviewModal(true)}
            />
          </Box>
        </Box>
        {showModal && videoUrl && (
          <WatchVideoModal commercial={commercial} edit={edit} videoUrl={videoUrl} setShowModal={setShowModal} />
        )}
        {showEditReviewModal && (
          <EditReviewModal commercial={commercial} edit={edit} setShowEditReviewModal={setShowEditReviewModal} />
        )}
        {showAssetSelector && (
          <AssetSelectorModal
            setShowModal={setShowAssetSelector}
            selectedAssetId={selectedAssetId}
            handleAssetSelect={handleAssetSelect}
          />
        )}
      </Box>
    );
  }
);
